import { Injectable } from '@angular/core';
import { RemittanceFileRecord } from '../../../models/remittances/remittance-file-record';
import { parse } from 'csv-parse';
import { read, utils } from 'xlsx';
import { MembersFileRecord } from '../../shared/components/batch-add-members-file-preview-modal/interfaces/members-file-record';

@Injectable()
export class FileToJsonService {
  public convertRemittanceFileToJson(file: File): Promise<RemittanceFileRecord[]> {
    const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.dat');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target?.result;
        if (isCSV) {
          resolve(this.convertRemittanceCsvToJson(content as string));
        } else if (isXLSX) {
          resolve(this.convertRemittanceXlsxToJson(content as ArrayBuffer));
        }
      };
      reader.onerror = () => {
        reject($localize`Error reading file.`);
      };

      if (isCSV) {
        reader.readAsText(file);
      } else if (isXLSX) {
        reader.readAsArrayBuffer(file);
      }
    });
  }

  private convertRemittanceCsvToJson(csvData: string): Promise<RemittanceFileRecord[]> {
    return new Promise((resolve, reject) => {
      parse(
        csvData,
        {
          columns: true,
          fromLine: 1,
          trim: true,
          cast: (value, context) => this.castRemittanceValue(context.column, value)
        },
        (error, records) => {
          if (error) {
            reject($localize`Error reading file.`);
          } else {
            resolve(records);
          }
        }
      );
    });
  }

  private castRemittanceValue(column: string | number | undefined, value: string): string | number | null {
    switch (column) {
      case 'AMOUNT':
        return this.parseCurrencyString(value);
      case 'ENTRY CODE':
        return this.parseEntryCode(value);
      default:
        return value;
    }
  }

  // This will maintain negative values
  private parseCurrencyString(value: string): number {
    const floatString = value.replace(/[,$\s]/g, '');

    return parseFloat(floatString);
  }

  private parseEntryCode(value: string): number | string {
    const parsedValue = parseInt(value, 10);
    return isNaN(parsedValue) ? value : parsedValue;
  }

  private convertRemittanceXlsxToJson(content: ArrayBuffer): Promise<RemittanceFileRecord[]> {
    const workbook = read(content, { type: 'buffer' });
    const sheetName = workbook.SheetNames.last();
    const sheet = workbook.Sheets[sheetName];
    const rows = utils.sheet_to_json<Record<string, string | null>>(sheet, { raw: false });
    const records = rows.map(row => {
      const record: Record<string, string | number | null> = {};
      Object.keys(row).forEach(key => {
        const column = key.trim();
        record[column] = this.castRemittanceValue(column, (row[key] ?? '').trim());
      });
      return record as unknown as RemittanceFileRecord;
    });
    return Promise.resolve(records);
  }

  // Member records
  private convertMembersCsvToJson(csvData: string): Promise<MembersFileRecord[]> {
    return new Promise((resolve, reject) => {
      parse(
        csvData,
        {
          columns: true,
          fromLine: 1,
          trim: true
        },
        (error, records) => {
          if (error) {
            reject($localize`Error reading file.`);
          } else {
            resolve(records);
          }
        }
      );
    });
  }

  private convertMembersXlsxToJson(content: ArrayBuffer): Promise<MembersFileRecord[]> {
    const workbook = read(content, { type: 'buffer' });
    const sheetName = workbook.SheetNames.last();
    const sheet = workbook.Sheets[sheetName];
    const jsonData = utils.sheet_to_json(sheet, { range: 1, raw: true }).map((record: any) => {
      Object.keys(record).forEach((key: any) => {
        if (typeof record[key] === 'string') {
          record[key] = record[key].trim();
        }
      });
      return record;
    });
    return Promise.resolve(jsonData);
  }

  public convertMemberFileToJson(file: File): Promise<MembersFileRecord[]> {
    const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.dat');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target?.result;
        if (isCSV) {
          resolve(this.convertMembersCsvToJson(content as string));
        } else if (isXLSX) {
          resolve(this.convertMembersXlsxToJson(content as ArrayBuffer));
        }
      };
      reader.onerror = () => {
        reject($localize`Error reading file.`);
      };

      if (isCSV) {
        reader.readAsText(file);
      } else if (isXLSX) {
        reader.readAsArrayBuffer(file);
      }
    });
  }
}
